import React, { useLayoutEffect, useState } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { BiomarkerItem } from "../../../../common/entities/type";
import {
  buildRangeSections,
  getBiomarkerHatchColors,
  getBiomarkerPointerColor,
  getNormalSectionIndex,
  getPointerPosition,
  getRangeValueSpan,
  getValueSectionIndex,
  hasValidRanges,
} from "./biomarkerDisplay";

/**
 * Segmented reference-range bar with a value pointer, as used on the Health
 * overview biomarker cards and the biomarker side panel header (16330:65608).
 *
 *   ──▁▁──▇▇▇▇▇▇▇▇──▁▁──     ← one section per backend range segment;
 *        ▼                     only the active one is full color
 *      3.5        5.5          ← the active section's limits
 *      └───────────┘           ← connected to the section they bound
 *
 * Sections, bounds and limits come from `item.ranges` (see buildRangeSections);
 * a biomarker without a valid ranges payload gets no bar.
 *
 * The active section is the one holding the current value, so an out-of-range
 * reading highlights the out-of-range section rather than the central one. With
 * no value the normal section is highlighted (the best-case scenario). Hovering
 * any section shows its limits and full color for as long as the pointer stays
 * on it.
 *
 * A range value ("<2.0") is an interval rather than a point, so it drops the
 * pointer and instead hatches the span of the bar it covers, bracketed above
 * (design 16333:62024); the normal section stays highlighted.
 *
 * A narrow section can be thinner than its two limits side by side, so the
 * labels are measured after layout and pushed apart around the section until
 * they no longer collide, then kept inside the bar's width.
 */
type Props = {
  item: BiomarkerItem;
};

const BiomarkerRangeBar = ({ item }: Props) => {
  const theme: Theme = useTheme();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  // A state ref, so the observer attaches whenever the row mounts (the bar
  // renders nothing until the item has valid ranges).
  const [limitsRow, setLimitsRow] = useState<HTMLDivElement | null>(null);
  const [rowWidth, setRowWidth] = useState(0);
  // Pixel left edges for the limit labels once measured; null falls back to
  // centering each label on its boundary.
  const [limitLefts, setLimitLefts] = useState<number[] | null>(null);

  useLayoutEffect(() => {
    if (!limitsRow || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) =>
      setRowWidth(entry.contentRect.width)
    );
    observer.observe(limitsRow);
    return () => observer.disconnect();
  }, [limitsRow]);

  // Re-run on every render: which labels show depends on hover and the value,
  // and the measurement is cheap. Only commit when the result changes.
  useLayoutEffect(() => {
    if (!limitsRow) return;
    const next = layoutLimitLabels(
      limitsRow,
      rowWidth || limitsRow.clientWidth
    );
    setLimitLefts((prev) =>
      prev &&
      next &&
      prev.length === next.length &&
      prev.every((v, i) => Math.abs(v - next[i]) < 0.5)
        ? prev
        : next
    );
  });

  if (item.qualitative) return null;
  // No valid reference ranges from the backend, no bar — including absolute
  // quantities (Dry Lean Mass, segmental masses…) that have no normal at all.
  // Drawing one anyway would state a range nobody sent.
  if (!hasValidRanges(item)) return null;

  const sections = buildRangeSections(theme, item);
  const pointerPos = getPointerPosition(sections, item);
  const pointerColor = getBiomarkerPointerColor(item.scoreCategory, theme);
  const rangeSpan = getRangeValueSpan(sections, item);
  const hatch = getBiomarkerHatchColors(item.scoreCategory, theme);

  const valueIndex = getValueSectionIndex(sections, item);
  // Hover wins while it lasts, then the value's section, then the normal one.
  const activeIndex =
    hoveredIndex ?? valueIndex ?? getNormalSectionIndex(sections);
  const active = sections[activeIndex];

  return (
    <div style={styles.wrapper}>
      {(pointerPos !== null || rangeSpan !== null) && (
        <div style={styles.pointerRow}>
          {rangeSpan ? (
            // The value spans an interval: a line over it with a tick dropping
            // onto each end it actually bounds.
            <div style={styles.rangeBracket(pointerColor, rangeSpan)} />
          ) : (
            <div style={styles.pointer(pointerColor, pointerPos as number)} />
          )}
        </div>
      )}
      <div style={styles.bar}>
        {rangeSpan && (
          <div style={styles.rangeHatch(hatch.fill, hatch.stripe, rangeSpan)} />
        )}
        {sections.map((section, idx) => (
          // The pill itself is 4px tall; the cell around it is the hover
          // target, so the sections stay comfortably hoverable.
          <div
            key={idx}
            style={styles.segmentCell(section.end - section.start)}
            onMouseEnter={() => setHoveredIndex(idx)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div style={styles.segment(section.color, idx === activeIndex)} />
          </div>
        ))}
      </div>
      {/* Limits sit above the connection line, each centered on the boundary
          it describes (design 16330:65608). */}
      <div ref={setLimitsRow} style={styles.limitsRow}>
        {active.limits.length > 0 ? (
          active.limits.map((limit, idx) => (
            <span
              key={idx}
              data-at={limit.at}
              style={styles.limitText(theme, limit.at, limitLefts?.[idx])}
            >
              {limit.text}
            </span>
          ))
        ) : (
          <span
            data-at={(active.start + active.end) / 2}
            style={styles.limitText(
              theme,
              (active.start + active.end) / 2,
              limitLefts?.[0]
            )}
          >
            {active.label}
          </span>
        )}
      </div>
      <div style={styles.connectorRow}>
        <div
          style={styles.connector(
            theme,
            active.start,
            active.end,
            active.openStart,
            active.openEnd
          )}
        />
      </div>
    </div>
  );
};

export default BiomarkerRangeBar;

// Minimum space between two limit labels.
const LIMIT_GAP = 8;

/**
 * Left edges (px) for the limit labels in `row`, each ideally centered on its
 * boundary (`data-at`, 0–1). Labels that would collide are spread apart
 * evenly around the point between them; the set is then shifted to stay
 * inside the row.
 */
const layoutLimitLabels = (row: HTMLElement, width: number) => {
  const labels = Array.from(row.children) as HTMLElement[];
  if (labels.length === 0 || width <= 0) return null;

  const widths = labels.map((el) => el.offsetWidth);
  const lefts = labels.map(
    (el, i) => Number(el.dataset.at) * width - widths[i] / 2
  );

  for (let i = 1; i < lefts.length; i++) {
    const overlap = lefts[i - 1] + widths[i - 1] + LIMIT_GAP - lefts[i];
    if (overlap > 0) {
      lefts[i - 1] -= overlap / 2;
      lefts[i] += overlap / 2;
    }
  }

  const last = lefts.length - 1;
  const overflowRight = lefts[last] + widths[last] - width;
  if (overflowRight > 0) lefts.forEach((_, i) => (lefts[i] -= overflowRight));
  if (lefts[0] < 0) {
    const shift = -lefts[0];
    lefts.forEach((_, i) => (lefts[i] += shift));
  }
  return lefts;
};

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column" as const,
    width: "100%",
  },
  pointerRow: {
    position: "relative" as const,
    height: "8px",
    width: "100%",
  },
  pointer: (color: string, position: number) => ({
    position: "absolute" as const,
    left: `${position * 100}%`,
    transform: "translateX(-50%)",
    width: 0,
    height: 0,
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderTop: `7px solid ${color}`,
  }),
  // The pills are 4px tall; the row is 20px so each section is a comfortable
  // hover target without changing how the bar looks.
  bar: {
    position: "relative" as const,
    display: "flex",
    width: "100%",
    height: "20px",
    alignItems: "center" as const,
    gap: "2px",
  },
  // Pills share a bottom edge (flex-end), so the active one grows upward
  // instead of pushing the row around. The padding keeps that shared edge
  // where a centered 4px pill used to sit, leaving the layout unchanged.
  segmentCell: (width: number) => ({
    flexGrow: width,
    flexBasis: 0,
    height: "100%",
    display: "flex",
    alignItems: "flex-end" as const,
    paddingBottom: "8px",
    boxSizing: "border-box" as const,
  }),
  // Inactive sections are the same color at half strength, as in the design —
  // the active one is the only one at full opacity, and a little taller.
  segment: (color: string, isActive: boolean) => ({
    backgroundColor: color,
    opacity: isActive ? 1 : 0.5,
    width: "100%",
    height: isActive ? "5px" : "4px",
    borderRadius: isActive ? "3px" : "2px",
  }),
  // Range value: the covered span of the bar, hatched in its category color,
  // sitting on the same bottom edge as the pills it covers.
  rangeHatch: (
    fill: string,
    stripe: string,
    span: { from: number; to: number }
  ) => ({
    position: "absolute" as const,
    left: `${span.from * 100}%`,
    width: `${(span.to - span.from) * 100}%`,
    bottom: "8px",
    // A touch taller than the pills so it fully covers the sections it spans,
    // including the active one.
    height: "5px",
    borderRadius: "3px",
    zIndex: 1,
    background: `repeating-linear-gradient(110deg, ${stripe} 0 2px, ${fill} 2px 4px)`,
  }),
  // The pointer for a range value: a line above the span with a tick dropping
  // onto the ends it bounds ("Vector 337" in the design). An end that runs to
  // infinity gets no tick.
  rangeBracket: (
    color: string,
    span: { from: number; to: number; openStart: boolean; openEnd: boolean }
  ) => ({
    position: "absolute" as const,
    left: `${span.from * 100}%`,
    width: `${(span.to - span.from) * 100}%`,
    bottom: 0,
    height: "6px",
    borderTop: `1px solid ${color}`,
    borderLeft: span.openStart ? "none" : `1px solid ${color}`,
    borderRight: span.openEnd ? "none" : `1px solid ${color}`,
  }),
  limitsRow: {
    position: "relative" as const,
    height: "16px",
    width: "100%",
  },
  limitText: (theme: Theme, at: number, leftPx?: number) => ({
    position: "absolute" as const,
    ...(leftPx !== undefined
      ? { left: `${leftPx}px` }
      : { left: `${at * 100}%`, transform: "translateX(-50%)" }),
    top: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "15px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "nowrap" as const,
  }),
  connectorRow: {
    position: "relative" as const,
    height: "8px",
    width: "100%",
    marginTop: "2px",
  },
  // "Vector 336" in the design: a line below the limits with a tick rising at
  // each end, spanning the active section. An open end drops its tick — that
  // side of the section runs to infinity, so there is no bound to mark.
  connector: (
    theme: Theme,
    start: number,
    end: number,
    openStart: boolean,
    openEnd: boolean
  ) => {
    const tick = `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`;
    return {
      position: "absolute" as const,
      left: `${start * 100}%`,
      width: `${(end - start) * 100}%`,
      height: "100%",
      borderLeft: openStart ? "none" : tick,
      borderRight: openEnd ? "none" : tick,
      borderBottom: tick,
    };
  },
};
